export type CharacterArchetype = "主角" | "配角" | "反派" | "群像";

export type CharacterItem = {
  id: string;
  name: string;
  romanizedName: string;
  workId: string;
  workTitle: string;
  avatar: string;
  archetype: CharacterArchetype;
  summary: string;
  quote: string;
  analysis: string[];
  traits: string[];
  updatedAt: string;
};

export const charactersList: CharacterItem[] = [
  {
    id: "c-001",
    name: "凌星野",
    romanizedName: "HOSHINO SEIYA",
    workId: "w-001",
    workTitle: "星海回响",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20young%20male%20pilot%20with%20messy%20dark%20hair%20and%20amber%20eyes%20in%20an%20orange%20flight%20suit%2C%20warm%20rim%20light%2C%20deep%20navy%20studio%20background%2C%20clean%20minimal%20portrait%20composition%2C%20detailed%20illustration&width=600&height=600&seq=age-char-001&orientation=squarish",
    archetype: "主角",
    summary:
      "从逃避责任到主动承担，凌星野的成长弧线是《星海回响》最动人的部分。他不是天生的英雄，只是一个勉强决定留下来的普通人。",
    quote: "「我不确定自己有没有资格，但这一次，我想把船开到终点。」",
    analysis: [
      "凌星野第一次出现时，正躲在一艘旧货船的货舱里。这个开场几乎概括了他的全部性格：习惯性回避，不擅长表达，也从未主动争取过什么。",
      "剧本对他的处理相当克制。第一季前半段，他每一次「站出来」都带着明显的犹豫，甚至会因为判断失误让队友陷入危险。作品没有急着为他安排高光时刻，而是先让他付出代价。",
      "第九集那场长达四分钟的独白，是他第一次完整地说出自己的恐惧。他没有说任何慷慨激昂的话，只是反复确认自己是否有资格继续留在船上。这种自我怀疑的真实感，远比任何热血台词更有力量。",
      "到了第二十四集，他的选择依然不是最完美的方案。但这一次，他清楚地知道自己在保护什么。所谓成长，大概就是从「被动地被推着走」，变成「主动地承担后果」。",
      "角色设计上，他的制服颜色从最初的灰蓝逐渐过渡到带着暖意的橙红。这种几乎不易察觉的变化，是制作组留给观众的一份温柔提示。",
    ],
    traits: ["内敛", "责任感", "自我怀疑", "成长型"],
    updatedAt: "2026-10-15",
  },
  {
    id: "c-002",
    name: "苏晚",
    romanizedName: "SU WAN",
    workId: "w-001",
    workTitle: "星海回响",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20calm%20young%20female%20navigator%20with%20short%20dark%20hair%20and%20a%20navy%20uniform%2C%20delicate%20technical%20earpiece%2C%20warm%20coral%20side%20light%2C%20clean%20minimal%20studio%20background%2C%20elegant%20illustration&width=600&height=600&seq=age-char-002&orientation=squarish",
    archetype: "配角",
    summary:
      "货船的领航员，也是最清楚这条航线有多危险的人。她用近乎冷漠的理性保护着船上所有人，包括那个还不确定要不要留下的人。",
    quote: "「航线没有问题，有问题的是我们为什么要出发。」",
    analysis: [
      "苏晚是船上最早出场的老成员之一。她的台词极少，但每一句都精确得像导航数据。观众最初很容易把她当成功能性角色，直到第五集那段简短的回忆。",
      "她的理性并非天生。回忆片段显示，她曾因为一次判断失误失去过整支小队。从那之后，她开始用近乎苛刻的方式检查每一个参数，也不再轻易对任何人许诺。",
      "她与凌星野的关系之所以好看，是因为剧本从未把它写成救赎。她不会鼓励他，也不会替他做决定，只是在他做出选择之后，安静地调整航线。",
      "这种克制的表达，让两人之间的信任显得格外有分量。",
    ],
    traits: ["冷静", "专业", "克制", "守护者"],
    updatedAt: "2026-10-13",
  },
  {
    id: "c-003",
    name: "黑川凛",
    romanizedName: "KUROKAWA RIN",
    workId: "w-002",
    workTitle: "赤月战线",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20fierce%20young%20female%20swordswoman%20with%20long%20black%20hair%20and%20a%20crimson%20scarf%2C%20determined%20expression%2C%20warm%20red%20rim%20light%20against%20deep%20navy%20background%2C%20cinematic%20illustration%2C%20high%20detail&width=600&height=600&seq=age-char-003&orientation=squarish",
    archetype: "主角",
    summary:
      "边境剑士团的现任队长，也是团里最年轻的成员。她的每一次胜利都伴随着明确的代价，而剧本从未试图掩盖这一点。",
    quote: "「刀不会替你承担后果，所以我必须记得每一次挥下去的重量。」",
    analysis: [
      "黑川凛的强大是被迫的。赤月异象出现时，剑士团的前任队长在第一时间失去了战斗力，作为副队长的她只能接过指挥权。",
      "作品的聪明之处在于，它没有把这份强大浪漫化。第三集那场被观众称为本季最佳的战斗之后，她花了整整一集的时间处理伤口，以及向阵亡者的家属交代。",
      "她的刀法风格也在随剧情变化。初期依赖爆发力，后期逐渐转向更节省体力的守势。这种变化没有被台词点明，而是通过分镜与作画细节呈现。",
      "在赤月之夜的最后一战里，她做出了一个不太符合「主角」身份的选择。但正是这个选择，让整部作品的主题得以成立。",
    ],
    traits: ["强悍", "务实", "背负责任", "行动派"],
    updatedAt: "2026-10-12",
  },
  {
    id: "c-004",
    name: "夏目桐",
    romanizedName: "NATSUME KIRI",
    workId: "w-003",
    workTitle: "雨巷食堂",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20quiet%20young%20woman%20with%20a%20low%20ponytail%20wearing%20a%20cream%20apron%2C%20standing%20in%20soft%20warm%20lantern%20light%2C%20deep%20navy%20shadows%2C%20gentle%20calm%20expression%2C%20cozy%20editorial%20illustration&width=600&height=600&seq=age-char-004&orientation=squarish",
    archetype: "主角",
    summary:
      "雨巷食堂的店主，话很少，做菜很慢。她的过去在整季中被反复暗示，却始终没有被完整交代。",
    quote: "「先吃点东西吧，其他的事可以慢慢说。」",
    analysis: [
      "夏目桐几乎不主动讲述自己的故事。她最常说的是「稍等一下」，然后把注意力放回灶台。这种沉默让她看起来像一位旁观者，而不是主角。",
      "但作品通过细节透露了很多：她切菜时惯用的手势、对某道菜近乎执拗的做法、以及在听到某首老歌时短暂的停顿。这些碎片拼出了一个不太完整的过去。",
      "她不试图治愈任何人。客人哭的时候，她只是把纸巾放在桌边，然后走开。这种不介入的温柔，反而让很多人愿意再回来一次。",
      "第十二集的最后一个镜头，她坐在打烊后的店里，第一次为自己做了一顿饭。整季的情绪在那个安静的瞬间完成了收束。",
    ],
    traits: ["沉静", "温柔", "有距离感", "生活感"],
    updatedAt: "2026-10-10",
  },
  {
    id: "c-005",
    name: "阿尔文",
    romanizedName: "ALVIN",
    workId: "w-004",
    workTitle: "剑鸣之诗",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20weathered%20young%20swordsman%20with%20short%20brown%20hair%20and%20a%20navy%20cloak%2C%20holding%20a%20worn%20blade%20over%20his%20shoulder%2C%20golden%20sunset%20light%2C%20clean%20minimal%20background%2C%20painterly%20cinematic%20illustration&width=600&height=600&seq=age-char-005&orientation=squarish",
    archetype: "主角",
    summary:
      "剑士团解散后，他在边境小镇以修理武器为生。剧场版用一段告别，补完了这个角色一直没说出口的部分。",
    quote: "「剑修好了。至于握剑的理由，得你自己找回来。」",
    analysis: [
      "电视版中的阿尔文是团队里最坚定的那一个。剧场版把他的时间线推到三年之后，让观众看到一个失去了目标的人如何生活。",
      "他不再战斗，却每天修理别人送来的武器。这个设定相当耐人寻味：他仍然和剑待在一起，只是换了一种方式。",
      "剧场版最动人的段落不是战斗，而是他在修理一把旧剑时，发现剑柄内侧刻着自己昔日的队友名字。整场戏没有一句台词，只有打磨金属的声音。",
      "结尾他重新握剑，但并非为了战斗，而是为了护送最后一程。这个选择让角色的弧线回到了他最本质的地方。",
    ],
    traits: ["沉稳", "念旧", "克制", "匠人气质"],
    updatedAt: "2026-10-08",
  },
  {
    id: "c-006",
    name: "白砚",
    romanizedName: "BAI YAN",
    workId: "w-005",
    workTitle: "灵能事务所",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20mysterious%20man%20in%20a%20dark%20tailored%20coat%2C%20holding%20a%20glowing%20paper%20talisman%2C%20cold%20navy%20lighting%20with%20a%20warm%20amber%20spark%2C%20clean%20minimal%20background%2C%20cinematic%20illustration&width=600&height=600&seq=age-char-006&orientation=squarish",
    archetype: "反派",
    summary:
      "灵能事务所的委托人，也是所有案件的真正源头。他并不认为自己在作恶，这才是这个角色最令人不安的地方。",
    quote: "「你们叫它异常，只是因为它不在你们的记录里。」",
    analysis: [
      "白砚在前半季里以委托人的身份出现过三次。每一次，他都带着一个看似合理、细节却处处对不上的故事。观众与主角一样，直到第八集才意识到这些委托之间的联系。",
      "他的动机并不复杂：他想找回一段被官方档案彻底抹去的记忆。为此，他不介意把整座旧城区当成实验场。",
      "剧本没有给他安排悲惨的童年回忆，也没有试图让观众同情他。他的可怕之处在于逻辑自洽——他所做的一切，在他的价值体系里都是合理的。",
      "最终对决没有发生在他与主角之间，而是在他与一份被公开的档案之间。这个设计让反派的落败显得格外有意味。",
    ],
    traits: ["缜密", "偏执", "优雅", "立场坚定"],
    updatedAt: "2026-09-30",
  },
  {
    id: "c-007",
    name: "阿澈",
    romanizedName: "CHE",
    workId: "w-007",
    workTitle: "机械花园",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20quiet%20child%20in%20a%20simple%20linen%20shirt%20standing%20among%20glass%20mechanical%20flowers%2C%20warm%20amber%20light%2C%20deep%20navy%20shadows%2C%20delicate%20dreamy%20illustration%2C%20soft%20texture&width=600&height=600&seq=age-char-007&orientation=squarish",
    archetype: "主角",
    summary:
      "温室唯一的照料者，也是唯一还记得温室来历的人。一个安静的孩子，用四季的时间学会告别。",
    quote: "「它们不会记得我，但我记得它们开过。」",
    analysis: [
      "阿澈几乎不说话。在前两集中，他的全部台词不超过二十句，更多的是动作：浇水、修剪、校准温度。",
      "作品用机械花的开合来表达他的情绪。当花朵第一次出现程序偏差时，他做的第一件事不是修理，而是把花搬到窗边，让它多晒一会儿太阳。",
      "第三集揭示了他的身份：他本身也是温室系统的一部分，一个被设计来照料花园的存在。这个设定让此前所有的安静都有了另一层含义。",
      "最后一集，他选择让花园按照自然的节奏枯萎。这不是放弃，而是他第一次做出了属于自己的决定。",
    ],
    traits: ["安静", "细腻", "观察者", "非人类"],
    updatedAt: "2026-09-24",
  },
  {
    id: "c-008",
    name: "云翼",
    romanizedName: "YUN YI",
    workId: "w-008",
    workTitle: "山海异闻录",
    avatar: "https://readdy.ai/api/search-image?query=Chinese%20ink%20wash%20anime%20character%20portrait%20of%20a%20young%20scholar%20in%20pale%20linen%20robes%20holding%20a%20scroll%2C%20misty%20mountain%20background%2C%20terracotta%20and%20deep%20indigo%20palette%2C%20elegant%20brush%20texture%2C%20refined%20illustration&width=600&height=600&seq=age-char-008&orientation=squarish",
    archetype: "主角",
    summary:
      "一个负责记录异闻的普通书生，不介入、不评判，只是把看到的一切写下来。他的克制，构成了整部作品的气质。",
    quote: "「我所记的，未必是真相，只是我亲眼所见。」",
    analysis: [
      "云翼不是传统意义上的天选之人。他没有法力，也不会武功，唯一的职责是记录。这个设定让作品得以用一种旁观者的视角展开。",
      "他的性格核心是「不介入」。第三集中，他遇到一只受伤的异兽，选择在远处守了整夜，却没有出手干预。这个决定在后来的剧情里被反复讨论。",
      "随着旅程推进，他的记录开始出现主观的痕迹。他会在某些条目后加上自己的判断，甚至会划掉重写。这种细微的变化，是角色内心松动的证明。",
      "结尾处，他把那卷手稿留在了一座无名的山庙里。没有交给任何人，也没有带走。",
    ],
    traits: ["温和", "克制", "记录者", "书卷气"],
    updatedAt: "2026-10-14",
  },
  {
    id: "c-009",
    name: "祁夜",
    romanizedName: "QI YE",
    workId: "w-009",
    workTitle: "零号列车",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20character%20portrait%20of%20a%20tall%20enigmatic%20man%20in%20a%20dark%20long%20coat%20standing%20inside%20a%20vintage%20train%20corridor%2C%20warm%20amber%20lamp%20light%2C%20deep%20navy%20shadows%2C%20subtle%20unsettling%20atmosphere%2C%20cinematic%20illustration&width=600&height=600&seq=age-char-009&orientation=squarish",
    archetype: "反派",
    summary:
      "他是列车上最早醒来的乘客，也是唯一知道列车目的地的人。他的每一次「帮助」，都让主角离真相更远一点。",
    quote: "「你以为你在找出口，其实你在找留下来的理由。」",
    analysis: [
      "祁夜在第二集就主动接近了主角，态度友善，提供了大量看似有用的信息。观众很容易把他当成引路人，直到第五集的那次站台停靠。",
      "他对列车的了解远超其他人，却从不解释来源。他的说法始终模糊，但在关键节点上又总能给出准确的提示。",
      "第八集揭示了他的真实身份：他并非乘客，而是列车运行系统的一部分，负责维持秩序。他的行为逻辑从来不针对某个人，只针对「失衡」本身。",
      "这个反派的悲剧性在于，他比任何人都更想离开这列车，却无法离开。",
    ],
    traits: ["神秘", "理性", "引导者", "被困者"],
    updatedAt: "2026-10-06",
  },
];

export const characterArchetypes: Array<CharacterArchetype | "全部"> = ["全部", "主角", "配角", "反派", "群像"];

export const getCharacterById = (id?: string) => charactersList.find((item) => item.id === id);